/**
 * @file: CustomerInfoEditor.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * A lightweight centered editor for the lead's customer name + phone.
 */

import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useEffect, useState } from 'react';
import {
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  TextInput,
  View,
} from 'react-native';
import { ActionButton } from './common/ActionButton';
import { LD } from './leadDetailKit';

export const CustomerInfoEditor = ({
  visible,
  initialName,
  initialPhone,
  onClose,
  onSave,
}: {
  visible: boolean;
  initialName: string;
  initialPhone: string;
  onClose: () => void;
  onSave: (name: string, phone: string) => void;
}) => {
  const { rpx } = useResponsiveContext();
  const [name, setName] = useState(initialName);
  const [phone, setPhone] = useState(initialPhone);
  // Reseed the fields each time the editor opens.
  useEffect(() => {
    if (visible) {
      setName(initialName);
      setPhone(initialPhone);
    }
  }, [visible, initialName, initialPhone]);

  const inputStyle = {
    backgroundColor: LD.canvas,
    borderRadius: rpx(10),
    paddingHorizontal: rpx(12),
    height: rpx(44),
    fontSize: rpx(15),
    color: LD.ink,
    marginTop: rpx(8),
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        style={{ flex: 1 }}
      >
        <Pressable
          style={{ flex: 1, backgroundColor: 'rgba(15,23,42,0.45)' }}
          onPress={onClose}
        />
        <View
          style={{
            position: 'absolute',
            left: rpx(24),
            right: rpx(24),
            top: '32%',
            backgroundColor: LD.surface,
            borderRadius: rpx(16),
            padding: rpx(18),
          }}
        >
          <Typography.Text size={rpx(16)} color={LD.ink} bold>
            编辑客户信息
          </Typography.Text>

          <Typography.Text size={rpx(12)} color={LD.mist} mt={rpx(14)}>
            客户名称
          </Typography.Text>
          <TextInput
            value={name}
            onChangeText={setName}
            placeholder="填写客户名称"
            placeholderTextColor={LD.mist}
            style={inputStyle}
          />

          <Typography.Text size={rpx(12)} color={LD.mist} mt={rpx(14)}>
            联系电话
          </Typography.Text>
          <TextInput
            value={phone}
            onChangeText={setPhone}
            placeholder="填写联系电话"
            placeholderTextColor={LD.mist}
            keyboardType="phone-pad"
            style={inputStyle}
          />

          <View
            style={{
              flexDirection: 'row',
              gap: rpx(10),
              marginTop: rpx(20),
            }}
          >
            <ActionButton label="取消" tone="secondary" onPress={onClose} />
            <ActionButton
              label="保存"
              tone="primary"
              onPress={() => onSave(name, phone)}
            />
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};
